/** Build a cube from its two free axes; `s` is derived to satisfy q+r+s=0. */
export function cube(q, r) {
    return { q, r, s: -q - r };
}
/** Stable string key for Map/Set lookups, e.g. "1,-2". */
export function cubeKey(c) {
    return `${c.q},${c.r}`;
}
export function cubeEquals(a, b) {
    return a.q === b.q && a.r === b.r && a.s === b.s;
}
export function cubeAdd(a, b) {
    return { q: a.q + b.q, r: a.r + b.r, s: a.s + b.s };
}
export function cubeSubtract(a, b) {
    return { q: a.q - b.q, r: a.r - b.r, s: a.s - b.s };
}
export function cubeScale(c, k) {
    return { q: c.q * k, r: c.r * k, s: c.s * k };
}
/**
 * The six neighbour directions in angular order. Consecutive indices are
 * adjacent edges of a hex — the node generator relies on that ordering to find
 * the three hexes that meet at each corner.
 */
export const CUBE_DIRECTIONS = [
    { q: 1, r: 0, s: -1 },
    { q: 1, r: -1, s: 0 },
    { q: 0, r: -1, s: 1 },
    { q: -1, r: 0, s: 1 },
    { q: -1, r: 1, s: 0 },
    { q: 0, r: 1, s: -1 },
];
/** Direction vector for `dir`, wrapping so any integer is valid. */
export function cubeDirection(dir) {
    return CUBE_DIRECTIONS[((dir % 6) + 6) % 6] ?? CUBE_DIRECTIONS[0];
}
export function cubeNeighbor(c, dir) {
    return cubeAdd(c, cubeDirection(dir));
}
/** Hex distance: number of single steps between two hexes. */
export function cubeDistance(a, b) {
    return (Math.abs(a.q - b.q) + Math.abs(a.r - b.r) + Math.abs(a.s - b.s)) / 2;
}
/** Ring distance from the centre (0,0,0): which concentric ring `c` sits on. */
export function cubeRingIndex(c) {
    return Math.max(Math.abs(c.q), Math.abs(c.r), Math.abs(c.s));
}
/**
 * Every hex whose ring distance from the centre is `<= radius`.
 * This set is exactly a regular hexagon of the given radius — the board.
 */
export function cubeRange(radius) {
    const out = [];
    for (let q = -radius; q <= radius; q++) {
        const rLo = Math.max(-radius, -q - radius);
        const rHi = Math.min(radius, -q + radius);
        for (let r = rLo; r <= rHi; r++)
            out.push(cube(q, r));
    }
    return out;
}
/**
 * Rotate a hex by one direction step about the centre. `rotateCW` advances to
 * the next `CUBE_DIRECTIONS` index; whether that reads as clockwise on screen
 * depends on your layout's y-axis (screen y points down). The board's six
 * triangular sectors are images of one another under repeated application.
 */
export function rotateCW(c) {
    return { q: -c.s, r: -c.q, s: -c.r };
}
export function rotateCCW(c) {
    return { q: -c.r, r: -c.s, s: -c.q };
}
//# sourceMappingURL=cube.js.map